var lite = require('dequal/lite');
var react = require('react');
var isEnv = require('@sitearcade/is-env');
var reactUse = require('react-use');

// import

function useCompare(val) {
  const ref = react.useRef(val);

  if (!lite.dequal(ref.current, val)) {
    ref.current = val;
  }

  return ref.current;
}
function useCompareSetter(state, setState) {
  return react.useCallback(v => lite.dequal(state, v) ? null : setState(v), []);
} // ...

const useCompareRef = useCompare;
function useCompareEffect(fn, deps) {
  return react.useEffect(fn, useCompare(deps));
}
function useCompareMemo(fn, deps) {
  return react.useMemo(fn, useCompare(deps));
}
function useCompareCallback(fn, deps = []) {
  return react.useCallback(fn, useCompare(deps));
}
function useCompareState(init) {
  const [state, setState] = react.useState(init);
  return [state, useCompareSetter(state, setState)];
}
function useCompareResult(fn) {
  const result = useCompare(fn());
  return react.useMemo(() => result, [result]);
}

// import

function debounce(fn, min = 0, max = 0) {
  let id = 0;
  let dead = false;
  let lastExec = 0;

  function handler() {
    if (dead) {
      return;
    }

    const elapsed = Date.now() - lastExec;
    const minDiff = Math.max(0, min - elapsed);
    const maxDiff = Math.max(0, max - elapsed);
    const delay = !id || max <= 0 ? minDiff : maxDiff > min ? min : maxDiff;
    clearTimeout(id);
    id = window.setTimeout(() => {
      fn(...[].slice.call(arguments));
      lastExec = Date.now();
      id = 0;
    }, delay);
  }

  handler.cancel = () => {
    clearTimeout(id);
    dead = true;
  };

  return handler;
} // export

function useDebounce(fn, wait = 0, leading = false) {
  const func = react.useRef(fn);
  func.current = fn;
  const id = react.useRef(0);
  react.useEffect(() => () => {
    clearTimeout(id.current);
    id.current = 0;
  }, [wait, leading]);
  return react.useCallback(function () {
    var args = [].slice.call(arguments);

    if (id.current === 0 && leading) {
      id.current = window.setTimeout(() => {
        id.current = 0;
      }, wait);
      func.current(...args);
    }

    clearTimeout(id.current);
    id.current = window.setTimeout(() => {
      id.current = 0;
      func.current(...args);
    }, wait);
  }, [wait, leading]);
}
function useDebounceState(initial, wait, leading) {
  const [state, setState] = react.useState(initial);
  return [state, useDebounce(setState, wait, leading)];
}

// import

const defOpts = {
  once: false,
  capture: true,
  passive: false
}; // export

function useEvents(fn, events = [], opts = defOpts, ms = 0) {
  const [domNode, setDomNode] = react.useState();
  react.useEffect(() => {
    if (!domNode || !fn) {
      return;
    }

    const handler = ms ? debounce(fn, ms, ms * 10) : fn;
    events.map(e => domNode.addEventListener(e, handler, opts));
    return () => {
      events.map(e => domNode.removeEventListener(e, handler));
      handler.cancel == null ? void 0 : handler.cancel();
    };
  }, [fn, events, ms, domNode, opts]);
  return react.useCallback(ref => setDomNode(ref), []);
}

// import

const noop$1 = () => undefined; // refs


function useIsMountRef() {
  const ref = react.useRef(true);
  react.useEffect(() => {
    ref.current = false;
  }, []);
  return ref;
}
function useIsMount() {
  return useIsMountRef().current;
}
function useIsUpdateRef() {
  const ref = react.useRef(false);
  react.useEffect(() => {
    ref.current = true;
  }, []);
  return ref;
}
function useIsUpdate() {
  return useIsUpdateRef().current;
} // state

function useIsMounted() {
  const ref = react.useRef(false);
  react.useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);
  return react.useCallback(() => ref.current, []);
} // effects

function useMountEffect(mountFn = noop$1) {
  react.useEffect(mountFn, []);
}
function useUpdateEffect(updateFn = noop$1, deps) {
  const isUpdate = useIsUpdate();
  const ref = react.useRef(updateFn);
  ref.current = updateFn;
  react.useEffect(() => {
    if (isUpdate) {
      ref.current();
    }
  }, deps);
}
function useUnmountEffect(unmountFn = noop$1) {
  const ref = react.useRef(unmountFn);
  ref.current = unmountFn;
  react.useEffect(() => ref.current, []);
}
function useLifecycleEffect(mountFn, unmountFn) {
  useMountEffect(mountFn);
  useUnmountEffect(unmountFn);
}

// import

const updateReducer = num => num++ % 1000; // lifecyle


function useForceUpdate() {
  const [, update] = react.useReducer(updateReducer, 0);
  return update;
}
function useUpdateOnMount() {
  const forceUpdate = useForceUpdate();
  useMountEffect(forceUpdate);
  return forceUpdate;
}

// import

function useHover(enabled = true) {
  const hoverRef = react.useRef();
  const [isHovered, setIsHovered] = react.useState(false);
  react.useEffect(() => {
    const {
      current
    } = hoverRef;

    const onMouseOver = () => setIsHovered(true);

    const onMouseOut = () => setIsHovered(false);

    if (enabled && current) {
      current.addEventListener('mouseover', onMouseOver);
      current.addEventListener('mouseout', onMouseOut);
    }

    return () => {
      if (enabled && current) {
        current.removeEventListener('mouseover', onMouseOver);
        current.removeEventListener('mouseout', onMouseOut);
      }
    };
  }, [enabled, hoverRef.current]);
  return [hoverRef, isHovered];
}

// import

function useMediaMatch(query) {
  const [isMatch, setIsMatch] = react.useState(false);
  react.useEffect(() => {
    if (isEnv.isServer) {
      return;
    }

    const matcher = window.matchMedia(query);
    const isModern = ('addEventListener' in matcher);

    const onUpdateMatch = ({
      matches
    }) => setIsMatch(matches);

    if (isModern) {
      matcher.addEventListener('change', onUpdateMatch);
    } else {
      matcher.addListener(onUpdateMatch);
    }

    onUpdateMatch(matcher);
    return () => {
      if (isModern) {
        matcher.removeEventListener('change', onUpdateMatch);
      } else {
        matcher.removeListener(onUpdateMatch);
      }
    };
  }, [query, setIsMatch]);
  return isMatch;
}

// import

const getPosition = (el, pos = {
  x: 0,
  y: 0
}) => {
  const newPos = {
    x: pos.x + el.offsetLeft - el.scrollLeft + el.clientLeft,
    y: pos.y + el.offsetTop - el.scrollTop + el.clientTop
  };
  return el.offsetParent ? getPosition(el.offsetParent, newPos) : newPos;
}; // hook


function useGetPosition() {
  const ref = react.useRef();
  const getPos = react.useCallback(() => ref.current ? getPosition(ref.current) : null, []);
  return [ref, getPos];
}

// import

const noop = () => undefined; // hooks


function useTimeoutEffect(timeoutFn = noop, ms = 0) {
  const timeout = react.useRef(0);
  const callback = react.useRef(timeoutFn);
  react.useEffect(() => {
    callback.current = timeoutFn;
  }, [timeoutFn]);
  react.useEffect(() => {
    clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => {
      callback.current();
    }, ms);
    return () => clearTimeout(timeout.current);
  }, [ms]);
}

// import

function useThrottle(fn, wait = 0, leading = false) {
  const func = react.useRef(fn);
  func.current = fn;
  const prev = react.useRef(0);
  const id = react.useRef(0);
  react.useEffect(() => () => {
    clearTimeout(id.current);
    prev.current = 0;
  }, [wait, leading]);
  return react.useCallback(function () {
    const now = Date.now();
    const current = prev.current;

    const call = () => {
      prev.current = now;
      clearTimeout(id.current);
      func.current(...[].slice.call(arguments));
    };

    if (leading && current === 0) {
      return call();
    }

    if (now - current > wait) {
      if (current > 0) {
        return call();
      }

      prev.current = now;
    }

    clearTimeout(id.current);
    id.current = window.setTimeout(() => {
      call();
      prev.current = 0;
    }, wait);
  }, [wait, leading]);
}
function useThrottleState(initial, wait, leading) {
  const [state, setState] = react.useState(initial);
  return [state, useThrottle(setState, wait, leading)];
}

// import

const listenerOpts$1 = {
  capture: false,
  passive: true
}; // export

function useWindowEvents(fn, events = [], ms = 0) {
  const [state, setState] = reactUse.useRafState(() => fn());
  react.useEffect(() => {
    const handler = debounce(e => setState(fn(e)), ms, ms * 10);
    events.map(e => window.addEventListener(e, handler, listenerOpts$1));
    return () => {
      events.map(e => window.removeEventListener(e, handler));
      handler.cancel();
    };
  }, [fn, events.join('-'), ms]);
  return state;
}

// import

const listenerOpts = {
  capture: false,
  passive: true
}; // export

function useWindowRadio(target, origin, onMessage) {
  react.useEffect(() => {
    const handler = event => origin === '*' || event.origin === origin ? onMessage == null ? void 0 : onMessage(event.data, event) : null;

    window.addEventListener('message', handler, listenerOpts);
    return () => window.removeEventListener('message', handler);
  }, [target, origin, onMessage]);
  return react.useCallback(msg => target.postMessage(msg, origin), [target, origin, onMessage]);
}
function useFrameRadio(origin, onMessage) {
  var _frameRef$current$con, _frameRef$current;

  const frameRef = react.useRef(null);
  const target = (_frameRef$current$con = (_frameRef$current = frameRef.current) == null ? void 0 : _frameRef$current.contentWindow) != null ? _frameRef$current$con : window;
  return [frameRef, useWindowRadio(target, origin, onMessage)];
}

// import

const events$1 = ['scroll', 'resize']; // fns

function getScroll() {
  return {
    x: isEnv.isClient ? window.pageXOffset : 0,
    y: isEnv.isClient ? window.pageYOffset : 0
  };
} // export


function useWindowScroll(ms) {
  return useWindowEvents(getScroll, events$1, ms);
}

// import

const events = ['resize']; // fns

function getSize() {
  return {
    width: isEnv.isClient ? window.innerWidth : 0,
    height: isEnv.isClient ? window.innerHeight : 0
  };
} // export


function useWindowSize(ms) {
  return useWindowEvents(getSize, events, ms);
}

exports.debounce = debounce;
exports.useCompare = useCompare;
exports.useCompareCallback = useCompareCallback;
exports.useCompareEffect = useCompareEffect;
exports.useCompareMemo = useCompareMemo;
exports.useCompareRef = useCompareRef;
exports.useCompareResult = useCompareResult;
exports.useCompareSetter = useCompareSetter;
exports.useCompareState = useCompareState;
exports.useDebounce = useDebounce;
exports.useDebounceState = useDebounceState;
exports.useEvents = useEvents;
exports.useForceUpdate = useForceUpdate;
exports.useFrameRadio = useFrameRadio;
exports.useGetPosition = useGetPosition;
exports.useHover = useHover;
exports.useIsMount = useIsMount;
exports.useIsMountRef = useIsMountRef;
exports.useIsMounted = useIsMounted;
exports.useIsUpdate = useIsUpdate;
exports.useIsUpdateRef = useIsUpdateRef;
exports.useLifecycleEffect = useLifecycleEffect;
exports.useMediaMatch = useMediaMatch;
exports.useMountEffect = useMountEffect;
exports.useThrottle = useThrottle;
exports.useThrottleState = useThrottleState;
exports.useTimeoutEffect = useTimeoutEffect;
exports.useUnmountEffect = useUnmountEffect;
exports.useUpdateEffect = useUpdateEffect;
exports.useUpdateOnMount = useUpdateOnMount;
exports.useWindowEvents = useWindowEvents;
exports.useWindowRadio = useWindowRadio;
exports.useWindowScroll = useWindowScroll;
exports.useWindowSize = useWindowSize;
//# sourceMappingURL=index.js.map
